/**
 * DEV-3224 (TDT-3483) — sunucuda hazırlanan rapor Excel'inin durumunu yoklar.
 *
 * Aynı yoklama mantığı üç yerde kopyalanmıştı (RaporDetayView bir, AI sihirbazı
 * iki) ve üçünde de aynı iki kusur vardı:
 *
 *  1. "bulunamadi" ilk yoklamada HATA sayılıyordu. Oysa kuyruğu tüketen işçi
 *     cron ile dakikada bir kalkıyor; iş sırasını bekliyor olabilir.
 *  2. Yoklamanın durma koşulu yoktu — iş hiç başlamazsa tarayıcı süresiz
 *     yokluyordu. Mesaj da yanıltıcıydı: "tekrar deneyin" demek kuyruğa bir iş
 *     daha eklemekten başka işe yaramıyordu.
 *
 * Kopyayı çoğaltmak yerine tek yere taşındı; bir dahaki düzeltme üç yeri
 * aramayı gerektirmesin (aynı uyarıyı üç yere yazmak, otomatikleştirmenin
 * yerine geçmez).
 */

/** Kuyruk işçisi cron ile dakikada bir kalkıyor; 5 dk hem onu hem üretimi kapsar. */
const TAVAN_MS = 5 * 60 * 1000;
const ARALIK_MS = 3000;

export interface SunucuExcelYoklamaKancalari {
  /** Durumu soran çağrı; 'hazir' | 'hata' | 'bulunamadi' | 'hazirlaniyor' | 'kuyrukta' döner. */
  durumSor: () => Promise<string>;
  /** Dosya hazır. */
  hazir: () => void;
  /** İş kalıcı olarak başarısız oldu. */
  hata: (mesaj: string) => void;
  /** Tavana ulaşıldı; iş hâlâ sürüyor olabilir. */
  zamanAsimi: (mesaj: string) => void;
}

const HATA_METNI = 'Excel üretilemedi. Bir sorun oluştu, lütfen yöneticinize bildirin.';
const INDIRME_HATA_METNI = 'Excel dosyasına ulaşılamadı. Lütfen yöneticinize bildirin.';
const TAVAN_METNI = 'Excel hâlâ hazır değil. Hazırlanmaya devam ediyor olabilir; birazdan tekrar bakın.';

export function sunucuExcelYokla(kancalar: SunucuExcelYoklamaKancalari, gecenMs: number = 0): void {
  setTimeout(() => {
    kancalar.durumSor().then((durum: string) => {
      if (durum === 'hazir') {
        kancalar.hazir();
        return;
      }
      if (durum === 'hata') {
        kancalar.hata(HATA_METNI);
        return;
      }
      // "bulunamadi" dahil diğer her durum: iş kuyrukta sırasını bekliyor olabilir.
      const yeniGecen = gecenMs + ARALIK_MS;
      if (yeniGecen >= TAVAN_MS) {
        kancalar.zamanAsimi(TAVAN_METNI);
        return;
      }
      sunucuExcelYokla(kancalar, yeniGecen);
    });
  }, ARALIK_MS);
}

/**
 * DEV-3396 (TDT-3483) — sunucudan inen gövdeyi ancak GERÇEKTEN elektronik tablo
 * ise kaydeder.
 *
 * Önceden iki çağrı yeri de gelen ne olursa olsun `.xlsx` adıyla kaydediyordu.
 * Sunucu hata gövdesi döndürdüğünde (durum "hazır" ama dosya okunamıyor) kullanıcı
 * 155 baytlık bir JSON'u "Excel" diye indiriyor ve açamıyordu; arızanın sunucuda
 * olduğu hiçbir yerde görünmüyordu.
 *
 * Durum koduna BAKILAMAZ: `$http` yanıt gövdesini döndürüyor (axios eklentisindeki
 * interceptor `response.data` veriyor), yani çağrı yerinde HTTP durumu yok. Bu
 * yüzden denetim gövdenin kendisinden yapılır.
 *
 * Ölçüt imza baytıdır, MIME tahmini değil: xlsx bir ZIP kabıdır ve daima `PK`
 * (0x50 0x4B) ile başlar. JSON/HTML hata gövdeleri başlamaz. MIME'a bakmak
 * güvenilmezdi — sunucu `application/octet-stream` de gönderebiliyor, hata
 * gövdesi de tipsiz gelebiliyor.
 */
export function sunucuExcelKaydet(
  govde: Blob,
  dosyaAdi: string,
  hata: (mesaj: string) => void,
): void {
  const okuyucu = new FileReader();
  okuyucu.onloadend = () => {
    const bas = new Uint8Array(okuyucu.result as ArrayBuffer);
    const zipMi = bas.length >= 2 && bas[0] === 0x50 && bas[1] === 0x4b;
    if (!zipMi) {
      hata(INDIRME_HATA_METNI);
      return;
    }
    const url = window.URL.createObjectURL(govde);
    const a = document.createElement('a');
    a.href = url;
    a.download = dosyaAdi;
    a.click();
    window.URL.revokeObjectURL(url);
  };
  // Hata gövdesi de okunamazsa yine kaydetme; sessizce bozuk dosya inmesin.
  okuyucu.onerror = () => hata(INDIRME_HATA_METNI);
  okuyucu.readAsArrayBuffer(govde.slice(0, 2));
}
